import { Box, Circle, HStack, Menu } from '@chakra-ui/react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '#modules/user/features/auth/hooks/useAuth';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { fetchFollowCounts, friendsQueryKeys } from '#modules/user/features/friends/model/friends';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { Link } from '#features/routing';
import { Avatar } from '#features/ui/components/Avatar';
import { IconButton } from '#features/ui/components/IconButton';
import { MenuContent } from '#features/ui/components/Menu';

export const UserSettings = () => {
  const { profile } = useProfile();
  const { logout } = useAuth();
  const isAuth = useIsAuth();

  const { t } = useLingui();

  /**
   * Раздел «Друзья» живёт в свёрнутом меню, поэтому без внешнего сигнала подписка разделит судьбу
   * приглашений в сессию: доходит, но не замечается. Точка на аватаре — этот сигнал.
   */
  const { data: counts } = useQuery({
    queryKey: friendsQueryKeys.counts,
    queryFn: fetchFollowCounts,
    enabled: Boolean(isAuth),
    staleTime: 60_000,
    retry: false,
  });

  /*
   * 🔴 Считаем НЕПРОСМОТРЕННЫХ, а не неотвеченных, и это не мелочь стиля.
   *
   * Раньше здесь стоял `newFollowers` — «подписался на меня, я в ответ нет». Это состояние, а не
   * новость: погасить его можно было только подпиской в ответ, поэтому точка висела у человека
   * месяцами и переставала что-либо значить. Так уведомления не делают ни в LinkedIn (точка на
   * «My Network»), ни в ВК (там счётчик получают заявки, а подписчики — вовсе нет).
   *
   * Число у ярлыка вкладки «Подписчики» при этом ОСТАЁТСЯ на `newFollowers`: оно подписывает
   * содержимое вкладки, и обнулять его от просмотра значит написать «Подписчики 0» над списком
   * из дюжины человек.
   */
  const unseenFollowers = counts?.unseenFollowers ?? 0;

  const onLogoutClick = async () => {
    logout();
  };

  return (
    <Menu.Root positioning={{ strategy: 'fixed', offset: { mainAxis: 20 } }}>
      <Menu.Trigger asChild>
        <IconButton variant="ghost" aria-label="Options" borderRadius="full" position="relative">
          <Avatar size="sm" src={profile?.avatar} name={profile?.displayName} />
          {unseenFollowers > 0 ? (
            <Box
              data-testid="new-followers-dot"
              position="absolute"
              top="0"
              right="0"
              boxSize="2.5"
              borderRadius="full"
              bgColor="purple.solid"
              borderWidth="2px"
              borderColor="bg"
            />
          ) : null}
        </IconButton>
      </Menu.Trigger>
      <MenuContent>
        <Menu.ItemGroup>
          <Menu.Item value="profile" asChild>
            <Link to="/{-$locale}/dashboard/profile">{t`My profile`}</Link>
          </Menu.Item>
          <Menu.Item value="friends" asChild>
            {/*
              Пока есть непросмотренные, пункт ведёт СРАЗУ на вкладку «Подписчики».
              Иначе сигнал про подписчика приводил на вкладку «Друзья» — у новичка пустую:
              человек видит точку, жмёт, никого не находит, и точка не гаснет. Выглядит как
              поломка, хотя всё работает. Ноль — ведём как раньше, на вкладку по умолчанию.
            */}
            <Link
              to="/{-$locale}/dashboard/friends"
              search={unseenFollowers > 0 ? { tab: 'followers' } : {}}
            >
              <HStack gap="2" justifyContent="space-between" w="100%">
                <span>
                  <Trans id="friends:title">Friends</Trans>
                </span>
                {unseenFollowers > 0 ? (
                  // Тот же кружок, что у непрочитанных чатов в шапке: одно событие — один язык.
                  <Circle
                    data-testid="new-followers-badge"
                    size="18px"
                    bg="purple.400"
                    color="white"
                    textStyle="micro"
                  >
                    {Math.min(unseenFollowers, 99)}
                  </Circle>
                ) : null}
              </HStack>
            </Link>
          </Menu.Item>
        </Menu.ItemGroup>
        <Menu.Separator />
        <Menu.Item value="feedback" asChild>
          <Link to="/{-$locale}/feedback">{t`dashboard:feedback.title`}</Link>
        </Menu.Item>
        <Menu.Item onClick={onLogoutClick} value="logout">
          {t`common:auth.logOut`}
        </Menu.Item>
      </MenuContent>
    </Menu.Root>
  );
};
